import { isMultipleChoice } from '@arnold/core';
import { QuestionType } from '../../generated/hooks';
import type { AverageScoring, MetricScoring, QuestionScoring, QuestionScoringMap, ReportQuestion } from './types';

export const isScoringValid = (question: ReportQuestion, scoring?: MetricScoring): boolean => {
  if (!scoring || ![QuestionType.Select, QuestionType.Scale].includes(question.type) || isMultipleChoice(question))
    return false;
  if (scoring.optionScores.length !== question.answersLocal?.length) return false;
  const values = scoring.optionScores.filter((score): score is number => score !== null);
  if (!values.length || values.some((score) => !Number.isFinite(score))) return false;
  if (scoring.kind === 'nps') {
    const minimum = scoring.minValue ?? 0;
    return (
      (minimum === 0 || minimum === 1) &&
      values.length === 11 - minimum &&
      new Set(values).size === 11 - minimum &&
      values.every((value) => Number.isInteger(value) && value >= minimum && value <= 10)
    );
  }
  return (
    Number.isFinite(scoring.min) &&
    Number.isFinite(scoring.max) &&
    scoring.max >= scoring.min &&
    values.every((value) => value >= scoring.min && value <= scoring.max)
  );
};

/** A leading number must end cleanly; don't read "1st", "1/5" or "1-10" as a rating of 1. */
function parseLeadingScore(label: string): number | undefined {
  const match = label
    .trim()
    .replace(/^−/, '-')
    .match(/^([+-]?\d+(?:[.,]\d+)?)(?=$|\s|\(|\.(?:\s|$))/);
  if (!match) return undefined;
  const value = Number(match[1].replace(',', '.'));
  return Number.isFinite(value) ? value : undefined;
}

function makeAverageScoring(optionScores: number[]): AverageScoring {
  return { kind: 'scale', optionScores, min: Math.min(...optionScores), max: Math.max(...optionScores) };
}

const defaultScaleOptionScores = (optionCount: number): number[] =>
  Array.from({ length: optionCount }, (_, index) => optionCount - index);

/** Parse all options, preserving their order. Numeric values define orientation: higher is better. */
export function inferQuestionScoring(question: ReportQuestion): QuestionScoring {
  const values = (question.answersLocal ?? []).map(parseLeadingScore);
  if (question.type === QuestionType.Scale && values.length) {
    const optionScores = defaultScaleOptionScores(values.length);
    const averageScoring = makeAverageScoring(optionScores);
    const nps = { kind: 'nps' as const, minValue: 0 as const, optionScores };
    return { average: averageScoring, nps: isScoringValid(question, nps) ? nps : undefined };
  }
  if (!values.length || values.some((value) => value === undefined)) return {};
  const optionScores = values as number[];
  const average = makeAverageScoring(optionScores);
  if (!isScoringValid(question, average)) return {};
  const nps = { kind: 'nps' as const, minValue: (Math.min(...optionScores) === 1 ? 1 : 0) as 0 | 1, optionScores };
  return { average, nps: isScoringValid(question, nps) ? nps : undefined };
}

/** Manual values are fallbacks, not overrides of numbers explicitly present in the current labels. */
export function resolveQuestionScoring(questions: ReportQuestion[], manual: QuestionScoringMap): QuestionScoringMap {
  return Object.fromEntries(
    questions.map((question) => {
      const automatic = inferQuestionScoring(question);
      return [
        question.index,
        {
          average: automatic.average ?? manual[question.index]?.average,
          nps: automatic.nps ?? manual[question.index]?.nps,
        },
      ];
    }),
  );
}
